import { Box, useTheme, useMediaQuery, Drawer, Typography, Button, CircularProgress } from '@mui/material';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import { tokens } from '../../global/theme/tokens';
import { useChatClient } from './useChatClient';
import { ChatHeader } from './ChatHeader';
import { ChatMessageList } from './ChatMessageList';
import { ChatMessageInput } from './ChatMessageInput';
import { ChatTypingIndicator } from './ChatTypingIndicator';
import { ChatConversationInfo } from './ChatConversationInfo';
import { ChatMessageSearch } from './ChatMessageSearch';
import { ChatMessageSearchResults } from './ChatMessageSearchResults';
import { useMessageSearch } from './useMessageSearch';
import { useState, useRef, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';

export const ChatMessageArea = ({ onBack }) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode, theme.palette.colorTheme);
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { t } = useTranslation();
  const {
    selectedConversation,
    messages,
    isLoading,
    typingUsersByConversation,
    isAtLatest,
    loadingDirection,
    pendingNewCount,
    jumpToMessage,
    returnToLatest,
  } = useChatClient();
  const conversationTypingUsers = (selectedConversation && typingUsersByConversation[selectedConversation.id]) || null;
  const [showInfo, setShowInfo] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const dragCounterRef = useRef(0);
  const messageInputRef = useRef(null);

  // A search is scoped to one conversation — switching conversations drops it
  useEffect(() => {
    setIsSearchOpen(false);
    setSearchQuery('');
  }, [selectedConversation?.id]);

  const handleSearchClose = useCallback(() => {
    setIsSearchOpen(false);
    setSearchQuery('');
  }, []);

  const search = useMessageSearch(
    isSearchOpen ? selectedConversation?.id : null,
    searchQuery
  );
  // Results take over the message area only once the query is long enough to have run;
  // an open-but-empty search still shows the conversation underneath.
  const showSearchResults = isSearchOpen && search.isActive;

  // Picking a result closes search and reopens the conversation at that message
  const handleResultSelect = useCallback(
    (message) => {
      setIsSearchOpen(false);
      setSearchQuery('');
      jumpToMessage(message);
    },
    [jumpToMessage]
  );

  const handleDragEnter = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounterRef.current++;
    if (e.dataTransfer?.types?.includes('Files')) {
      setIsDragging(true);
    }
  }, []);

  const handleDragLeave = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounterRef.current--;
    if (dragCounterRef.current === 0) {
      setIsDragging(false);
    }
  }, []);

  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    dragCounterRef.current = 0;

    const files = e.dataTransfer?.files;
    if (files && files.length > 0 && messageInputRef.current) {
      messageInputRef.current.addFiles(files);
    }
  }, []);

  if (!selectedConversation) return null;

  const InfoPanel = (
    <ChatConversationInfo
      conversation={selectedConversation}
      onClose={() => setShowInfo(false)}
      isMobile={isMobile}
    />
  );

  return (
    <Box
      className="flex h-full relative"
      onDragEnter={handleDragEnter}
      onDragLeave={handleDragLeave}
      onDragOver={handleDragOver}
      onDrop={handleDrop}
    >
      {/* Drop Zone Overlay */}
      {isDragging && (
        <Box
          className="absolute inset-0 z-50 flex flex-col items-center justify-center"
          sx={{
            backgroundColor: `${colors.info}15`,
            border: `3px dashed ${colors.info}`,
            borderRadius: '16px',
            margin: '8px',
            backdropFilter: 'blur(4px)',
          }}
        >
          <CloudUploadIcon sx={{ fontSize: 64, color: colors.info, mb: 2 }} />
          <Typography variant="h6" sx={{ color: colors.info, fontWeight: 600 }}>
            {t('Drop files here')}
          </Typography>
          <Typography variant="body2" sx={{ color: colors.text, opacity: 0.6, mt: 0.5 }}>
            {t('Release to upload')}
          </Typography>
        </Box>
      )}

      <Box className="flex flex-col flex-1 min-w-0">
        {/* Header — the search bar takes the whole row while it is open */}
        {isSearchOpen ? (
          <ChatMessageSearch
            query={searchQuery}
            onQueryChange={setSearchQuery}
            onClose={handleSearchClose}
            resultCount={search.isActive ? search.results.length : undefined}
            hasMore={search.hasMore}
            isSearching={search.status === 'searching'}
          />
        ) : (
          <ChatHeader
            conversation={selectedConversation}
            onBack={onBack}
            onInfoClick={() => setShowInfo(!showInfo)}
            onSearchOpen={() => setIsSearchOpen(true)}
          />
        )}

        {/* Messages, or the search results that stand in for them */}
        <Box className="flex-1 overflow-hidden relative">
          {showSearchResults ? (
            <ChatMessageSearchResults
              results={search.results}
              status={search.status}
              isLoadingMore={search.isLoadingMore}
              hasMore={search.hasMore}
              loadMore={search.loadMore}
              term={search.term}
              query={searchQuery}
              onSelect={handleResultSelect}
            />
          ) : (
            <ChatMessageList messages={messages} isLoading={isLoading} />
          )}

          {/* Floated over the list rather than placed in it: the downwards spinner would
              otherwise sit right where the scroll is anchored, and mounting it would
              shove the messages by its own height. */}
          {!showSearchResults && loadingDirection === 'newer' && (
            <Box
              className="flex justify-center"
              sx={{ position: 'absolute', bottom: 56, left: 0, right: 0 }}
            >
              <CircularProgress size={20} sx={{ color: colors.info }} />
            </Box>
          )}

          {/* Only shown while reading history, since the newest message is off-list */}
          {!showSearchResults && !isAtLatest && (
            <Button
              onClick={returnToLatest}
              size="small"
              startIcon={<ArrowDownwardIcon sx={{ fontSize: 16 }} />}
              sx={{
                position: 'absolute',
                bottom: 12,
                left: '50%',
                transform: 'translateX(-50%)',
                textTransform: 'none',
                borderRadius: '999px',
                px: 2,
                color: colors.text,
                backgroundColor: colors.glass,
                backdropFilter: 'blur(10px)',
                boxShadow: 3,
                '&:hover': { backgroundColor: colors.glassSolid },
              }}
            >
              {t('Jump to latest')}
              {pendingNewCount > 0 && ` (${pendingNewCount})`}
            </Button>
          )}
        </Box>

        {/* Typing Indicator — irrelevant while reading search results */}
        {!showSearchResults && (
          <ChatTypingIndicator typingUsers={conversationTypingUsers} />
        )}

        {/* Input */}
        <ChatMessageInput ref={messageInputRef} />
      </Box>

      {/* Info Panel - Drawer on mobile, side panel on desktop */}
      {isMobile ? (
        <Drawer
          anchor="right"
          open={showInfo}
          onClose={() => setShowInfo(false)}
          PaperProps={{
            sx: {
              width: '100%',
              maxWidth: 400,
              backgroundColor: colors.glass,
              backdropFilter: 'blur(10px)',
            },
          }}
        >
          {InfoPanel}
        </Drawer>
      ) : (
        showInfo && InfoPanel
      )}
    </Box>
  );
};
